import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from 'react-router-dom';
import { useMsal } from "@azure/msal-react";
import { axiosFetch, initializeAuth, saveAppDetails } from "services/apiService";
import { getRxApps, setRxAppData } from "redux/appCenterSlice";
import { AppApiResponse } from "models/AppApiResponse";
import DatePicker from "react-datepicker";
import { Pencil, Plus, Ban, SaveIcon, ChevronRight } from "lucide-react";
import { set } from "react-datepicker/dist/date_utils";

const API_URL = process.env.REACT_APP_API_URL || "";

function Apps() {
  const dispatch = useDispatch();
  const { instance, accounts } = useMsal();
  const [appList, setAppList] = useState<AppApiResponse[]>([]);
  const [appDetails, setAppDetails] = useState<AppApiResponse>();
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isReadOnly, setIsReadOnly] = useState<boolean>(true);
  const [ releaseDate, setReleaseDate ] = useState<Date | null>(null);
//  const rxApps = useSelector(getRxApps);

  useEffect(() => {
    loadApps();
  }, [] );

  useEffect(() => {
    dispatch(setRxAppData(appDetails!));
    setReleaseDate(appDetails?.releaseDate ? new Date(appDetails.releaseDate.indexOf("T") > 0 ? appDetails.releaseDate : appDetails.releaseDate + "T00:00:00") : null);
  }, [appDetails]);

  const loadApps = async () => {
    try {
//        setLoading(true);
//        setError(null);
      const response = await axiosFetch.get(`${API_URL}/engineering/applications`);
      const data = response.data;
      setAppList(data);
    } catch (error) {
      console.error('Error loading apps:', error);
      setError('Error loading apps. Please try again.');
    } finally {
//        setLoading(false);
    }
  };


  const appTypes: Array<string> = [ 'Desktop', 'Web', 'PowerBI', 'Reporting' ];

  const addApp = () => {
    setAppDetails({} as AppApiResponse);
    setIsReadOnly(false);
  };
  const editApp = () => {
    if (!appDetails) {
      setAppDetails({} as AppApiResponse);
    };
    setIsReadOnly(false);
  };
  const cancelEdit = () => {
    setAppDetails({} as AppApiResponse);
    loadApps();
    setIsReadOnly(true);
  };
  const saveChange = async(text: string) => {
    await initializeAuth(instance, accounts);
    await saveAppDetails(appDetails);
    setIsReadOnly(true);
    loadApps();
    setSuccessMessage("Application details saved successfully.");
    setTimeout(() => {
      setSuccessMessage(null);
    }, 3000);
  }

  const postCreationDate = (date: Date | null) => {
    const localDate = new Date(date!.toLocaleDateString());
    const adjustedDate = new Date(date!.setHours(12, 0, 0, 0));
    setReleaseDate(date);
    setAppDetails( { ...appDetails!, releaseDate: localDate.toISOString().split('T')[0] });
    
  }
  const setContent = <K extends keyof AppApiResponse>(appItem: AppApiResponse, field: K, value:  AppApiResponse[K]) => {
    setAppDetails({ ...appItem, [field]: value });
  };


  return (
    <div className="px-6 py-4 space-y-6">
      <div className="flex">
        <div className="flex bg-blue-600 rounded display-inline-block appButtons">
          <button className="p-2 text-white" onClick={(e) => addApp()}>
            <Plus size={20} />
          </button>
          <button className="p-2 text-white" disabled={!isReadOnly} onClick={(e) => editApp()}>
            <Pencil size={20} />
          </button>
          <button className="p-2 text-white" disabled={isReadOnly} onClick={(e) => cancelEdit()}>
            <Ban size={20} />
          </button>
          <button className="p-2 text-white" disabled={isReadOnly} onClick={(e) => saveChange("")}>
            <SaveIcon size={20} />
          </button>
        </div>
        <div className="flex pt-2 pl-4">
          {error && <div className="text-red-500">{error}</div>}
          {successMessage && <div className="text-blue-600">{successMessage}</div>}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <div className="text-left">
          <ul>
              {appList.map(row => (
                  <li key={row.id} className="tiny-font hover:text-blue-800 cursor-pointer" onClick={() => setAppDetails(row)}>
                      {row.name}
                  </li>
              ))}
          </ul>
        </div>
        <div className="col-span-3">
          <table className="apps w-full">
            <tbody>
              <tr>
                <td>Name</td>
                <td>
                  <input
                    type="text"
                    className="editable"
                    readOnly={isReadOnly}
                    value={appDetails?.name || ""}
                    onChange={(e) => setContent(appDetails!, 'name', e.target.value)}
                  />
                </td>
              </tr>
              <tr>
                <td>Description</td>
                <td>
                  <textarea
                    className="editable"
                    readOnly={isReadOnly}
                    value={appDetails?.description || ""}
                    onChange={(e) => setContent(appDetails!, 'description', e.target.value)}
                    rows={6} cols={60}
                  />
                </td>
              </tr>
              <tr>
                <td>Status</td>
                <td>
                  <select
                    className="editable"
                    disabled={isReadOnly}
                    value={appDetails ? appDetails?.status ? 'true' : 'false' : ''}
                    onChange={(e) => setContent(appDetails!, 'status', e.target.value === 'true')}
                  >
                    <option value=""></option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </td>
              </tr>
              <tr>
                <td>Version</td>
                <td>
                  <input
                    type="text"
                    className="editable"
                    readOnly={isReadOnly}
                    value={appDetails?.version || ""}
                    onChange={(e) => setContent(appDetails!, 'version', e.target.value)}
                  />
                </td>
              </tr>
              <tr>
                <td>Url</td>
                <td>
                  <input
                    type="text"
                    className="editable"
                    readOnly={isReadOnly}
                    value={appDetails?.url || ""}
                    onChange={(e) => setContent(appDetails!, 'url', e.target.value)}
                  />
                </td>
              </tr>
              <tr>
                <td>Developed By</td>
                <td>
                  <input
                    type="text"
                    className="editable"
                    readOnly={isReadOnly}
                    value={appDetails?.developedBy || ""}
                    onChange={(e) => setContent(appDetails!, 'developedBy', e.target.value)}
                  />
                </td>
              </tr>
              <tr>
                <td>Released Date</td>
                <td>
                  <DatePicker 
                    className="editable"
                    selected={releaseDate} 
                    readOnly={isReadOnly}
                    onChange={(date) => postCreationDate(date)} 
                    showMonthDropdown showYearDropdown dropdownMode="select"
                    dateFormat="MMM. d, yyyy"
                  />
                </td>
              </tr>
              <tr>
                <td>Languages</td>
                <td>{appDetails?.languages}</td>
              </tr>
              <tr>
                <td>Release Notes</td>
                <td>
                    <span contentEditable={!isReadOnly}  className="w-full"
                      onBlur={(e) => setContent(appDetails!, 'releaseNotes', e.currentTarget.innerHTML)} 
                      dangerouslySetInnerHTML={{__html: appDetails?.releaseNotes ?? ''}} />
                </td>
              </tr>
              <tr>
                <td>Tutorials</td>
                <td>
                  {appDetails?.name && !isReadOnly && (
                    <Link to={'/tutorials/' + appDetails?.name} rel="noopener noreferrer">
                      <ChevronRight className="h-5 w-5" />
                    </Link>
                  )}
                  {appDetails?.name && isReadOnly && (
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                  )}
                </td>
              </tr>
              <tr>
                <td>Documentation</td>
                <td>{appDetails?.documentation}</td>
              </tr>
              <tr>
                <td>Support</td>
                <td>
                  {appDetails?.name && !isReadOnly && (
                    <Link to={'/support/' + appDetails?.name} rel="noopener noreferrer">
                      <ChevronRight className="h-5 w-5" />
                    </Link>
                  )}
                  {appDetails?.name && isReadOnly && (
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                  )}
                </td>
              </tr>
              <tr>
                <td>FAQs</td>
                <td>
                  {appDetails?.name && !isReadOnly && (
                    <Link to={'/faqs/' + appDetails?.name} rel="noopener noreferrer">
                      <ChevronRight className="h-5 w-5" />
                    </Link>
                  )}
                  {appDetails?.name && isReadOnly && (
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                  )}
                </td>
              </tr>
              <tr>
                <td>Application Type</td>
                <td>
                  <div>
                    {appTypes.map((appType) => (
                      <label key={appType} className="mr-5">
                        <span>
                          <input
                            checked={appDetails?.applicationType === appType}
                            type="radio"
                            disabled={isReadOnly}
                            value={appType}
                            id={`field-${appType.toLowerCase()}`}
                            onChange={() => setContent(appDetails!, 'applicationType', appType as 'Desktop' | 'Web' | 'PowerBI' | 'Reporting')}
                          />
                          {appType}
                        </span>
                      </label>
                    ))}
                  </div>
                </td>
              </tr>
              <tr>
                <td>Details</td>
                <td>
                  {appDetails?.name && !isReadOnly && (
                    <Link to={'/details/' + appDetails?.name} rel="noopener noreferrer">
                      <ChevronRight className="h-5 w-5" />
                    </Link>
                  )}
                  {appDetails?.name && isReadOnly && (
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  ) 
}

export default Apps;
